import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Download, FileSpreadsheet, Package, CheckCircle2, AlertTriangle, X,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';
import DuplicatePendingModal from '@/components/DuplicatePendingModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function ValidationErrorRow({ row }) {
  const errs = (row.errors || []).join(' | ');
  return (
    <tr className="border-t border-red-100">
      <td className="px-3 py-1">{row.row_number}</td>
      <td className="px-3 py-1 text-red-700">{errs}</td>
    </tr>
  );
}

export default function BulkOrderUploadTab({ onImported }) {
  const [file, setFile] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [validation, setValidation] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  // Duplicate-pending pre-flight modal — warns admin when the CSV asks
  // for SKUs that the franchise already has on order. Warning only.
  const [dupModal, setDupModal] = useState(null);

  const downloadTemplate = () => {
    // 3-column simplified template (May 2026): franchise_id, product_id, quantity.
    // `product_id` accepts the canonical UUID, the human SKU code (e.g.
    // PROD-018835), or the legacy sku — the server resolves any of them to
    // the canonical product before storage.
    const csv = 'franchise_id,product_id,quantity\nFRN-001,PROD-018835,10\nFRN-001,PROD-000018,5\nFRN-002,PROD-018835,8';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'franchise_order_bulk_upload_template.csv';
    a.click();
    toast.success('Template downloaded');
  };

  const parseCSV = (text) => {
    const lines = text.replace(/^\uFEFF/, '').trim().split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i].split(',').map(c => c.trim());
      const row = { row_number: i + 1 };
      headers.forEach((h, idx) => { row[h] = cells[idx] ?? ''; });
      // Backwards compat: legacy CSVs used `product_sku` column. Map it
      // into `product_id` so the simplified 3-column schema still
      // accepts old templates without breaking the user's existing files.
      if (!row.product_id && row.product_sku) {
        row.product_id = row.product_sku;
      }
      row.quantity = parseFloat(row.quantity) || 0;
      rows.push(row);
    }
    return rows;
  };

  const handleFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setParsing(true); setValidation(null);
    try {
      const text = await f.text();
      const parsed = parseCSV(text);
      if (!parsed.length) { toast.error('Empty CSV'); setFile(null); return; }
      setRawData(parsed);
      toast.success(`Parsed ${parsed.length} rows`);
    } catch (err) { toast.error('Parse failed'); }
    finally { setParsing(false); }
  };

  const validate = async () => {
    setValidating(true);
    try {
      const res = await axios.post(`${API}/orders/bulk-validate`,
        { items: rawData },
        { headers: authHeaders() });
      setValidation(res.data);
      if (res.data.success) {
        toast.success(`All ${res.data.valid_rows} rows valid`);
      } else {
        toast.warning(`${res.data.error_rows} rows have errors`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Validation failed');
    } finally { setValidating(false); }
  };

  const doImport = async () => {
    if (!validation) return;
    // Group valid rows by franchise so we can probe each one for pending dupes.
    const validRows = (validation?.validated_items || []).filter(r => r.is_valid);
    const byFranchise = {};
    for (const r of validRows) {
      const fid = r.franchise_id || r.resolved_franchise_id;
      if (!fid || !r.product_id) continue;
      byFranchise[fid] = byFranchise[fid] || [];
      byFranchise[fid].push({ product_id: r.product_id, quantity: r.quantity });
    }
    // Probe in parallel — warning only, swallow individual failures.
    try {
      const probes = await Promise.all(
        Object.entries(byFranchise).map(([fid, items]) =>
          axios.post(`${API}/orders/check-duplicate-pending`,
            { franchise_id: fid, items },
            { headers: authHeaders() })
            .then((r) => r.data).catch(() => ({ matches: [] }))
        )
      );
      const allMatches = probes.flatMap(p => p.matches || []);
      const periodDays = probes.find(p => p.period_days)?.period_days;
      if (allMatches.length > 0) {
        setDupModal({ matches: allMatches, periodDays });
        return; // Modal's YES button calls confirmImport() below.
      }
    } catch (_) { /* non-blocking */ }
    await confirmImport();
  };

  const confirmImport = async () => {
    if (!validation) return;
    setDupModal(null);
    setImporting(true);
    try {
      const res = await axios.post(`${API}/orders/bulk-import`, validation,
        { headers: authHeaders() });
      toast.success(`Created ${res.data.count} order(s). Warehouse stock auto-allocated to Ready-for-Pick where available; use "Export Shortages" to raise POs manually for the rest.`);
      setFile(null); setRawData([]); setValidation(null);
      if (onImported) onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const validCount = validation?.valid_rows ?? 0;
  const errCount = validation?.error_rows ?? 0;
  const errorItems = (validation?.validated_items || []).filter(r => !r.is_valid);

  return (
    <div className="space-y-4" data-testid="orders-bulk-upload">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-1" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-blue-900">Bulk Order Upload</p>
            <ul className="list-disc ml-5 mt-1 text-blue-800">
              <li>Columns: <code>pr_id, franchise_id, product_sku, quantity</code></li>
              <li>Franchise ID may be the FRN-XXXX-… business ID or the internal onboarding id</li>
              <li>Product SKU should match Master Catalogue</li>
              <li>System groups rows by franchise → creates one Order per franchise</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex gap-3 items-center">
        <Button variant="outline" onClick={downloadTemplate} data-testid="download-order-template">
          <Download className="w-4 h-4 mr-1" />Download Template
        </Button>
        <div className="flex-1">
          <Input type="file" accept=".csv" onChange={handleFile} disabled={parsing}
            data-testid="order-file-input" />
        </div>
        {file && (
          <Button variant="ghost" onClick={() => { setFile(null); setRawData([]); setValidation(null); }}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {rawData.length > 0 && !validation && (
        <div className="border rounded-lg p-3 bg-slate-50 flex items-center justify-between">
          <span className="text-sm">{rawData.length} rows parsed — click Validate to check</span>
          <Button onClick={validate} disabled={validating} data-testid="validate-orders-btn">
            <CheckCircle2 className="w-4 h-4 mr-1" />{validating ? 'Validating…' : 'Validate Data'}
          </Button>
        </div>
      )}

      {validation && (
        <div className="space-y-3">
          <div className={`p-3 rounded-lg border ${validation.success ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <span className="font-semibold">{validation.success ? 'All rows valid' : 'Some rows have errors'}</span>
                <span className="text-sm ml-3">
                  <span className="text-green-700">{validCount} valid</span>
                  {errCount > 0 && <span className="text-red-700 ml-3">{errCount} errors</span>}
                </span>
              </div>
              {validCount > 0 && (
                <Button onClick={doImport} disabled={importing} data-testid="create-orders-btn">
                  <Package className="w-4 h-4 mr-1" />
                  {importing ? 'Importing…' : `Create ${validCount} Order Line${validCount === 1 ? '' : 's'}`}
                </Button>
              )}
            </div>
          </div>
          {errorItems.length > 0 && (
            <div className="border border-red-200 rounded-lg overflow-hidden">
              <div className="bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />Validation errors
              </div>
              <table className="w-full text-sm">
                <thead className="bg-red-100">
                  <tr><th className="px-3 py-1 text-left">Row</th><th className="px-3 py-1 text-left">Error(s)</th></tr>
                </thead>
                <tbody>
                  {errorItems.map(row => <ValidationErrorRow key={row.row_number} row={row} />)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <DuplicatePendingModal
        open={!!dupModal}
        matches={dupModal?.matches || []}
        periodDays={dupModal?.periodDays}
        onCancel={() => setDupModal(null)}
        onConfirm={confirmImport}
        testIdPrefix="erp-dup"
      />
    </div>
  );
}
